/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import { Menu, Image } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import UserRoleMngtActiveIcon from '../../assets/icons/user-role-active-icon.svg';
import UserRoleMngtIcon from '../../assets/icons/user-role-icon.svg';
import EnqMngtActiveIcon from '../../assets/icons/enq-mngt-active-icon.svg';
import EnqMngtIcon from '../../assets/icons/enq-mngt-icon.svg';
import BusinessIcon from '../../assets/icons/business-icon.svg';
import BusinessActiveIcon from '../../assets/icons/business-active-icon.svg';
import InvestorActiveIcon from '../../assets/icons/investor-active-icon.svg';
import InvestorIcon from '../../assets/icons/investor-icon.svg';
import EntActiveIcon from '../../assets/icons/ent-active-icon.svg';
import EntIcon from '../../assets/icons/ent-icon.svg';
import CreditLimitActiveIcon from '../../assets/icons/credit-limit-active-icon.svg';
import CreditLimitIcon from '../../assets/icons/credit-limit-icon.svg';
import WalletActiveIcon from '../../assets/icons/wallet-active-icon.svg';
import WalletIcon from '../../assets/icons/wallet-icon.svg';
import BankActiveIcon from '../../assets/icons/bank-active-icon.svg';
import BankIcon from '../../assets/icons/bank-icon.svg';
import InvUploadActiveIcon from '../../assets/icons/inv-upload-active-icon.svg';
import InvUploadIcon from '../../assets/icons/inv-upload-icon.svg';
import DealsActiveIcon from '../../assets/icons/deals-active-icon.svg';
import DealsIcon from '../../assets/icons/deals-icon.svg';
import SettlementActiveIcon from '../../assets/icons/settlement-active-icon.svg';
import SettlementIcon from '../../assets/icons/settlement-icon.svg';
import PaymentActiveIcon from '../../assets/icons/payment-active-icon.svg';
import PaymentIcon from '../../assets/icons/payment-icon.svg';
import ReportsActiveIcon from '../../assets/icons/reports-active-icon.svg';
import ReportsIcon from '../../assets/icons/reports-icon.svg';
import UsersActiveIcon from '../../assets/icons/users-active-icon.svg';
import UsersIcon from '../../assets/icons/users-icon.svg';
import RmActiveIcon from '../../assets/icons/rm-active-icon.svg';
import RmIcon from '../../assets/icons/rm-icon.svg';
import './Sidebar.scss';
import { PERM_CODE } from '../../constants/PermissionCodes';
import { count } from '../../redux/slices/AuthSlice';


const SidebarContent = ({ onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [selectedKey, setSelectedKey] = useState(location.pathname);
    const { user, allCountData } = useSelector((state) => state.auth);
    const { creditLimitData, requestsData } = useSelector((state) => state?.creditLimit || {});

    // 🔥 Fetch count when Sidebar loads
    useEffect(() => {
        dispatch(count("ALL"));
    }, [dispatch]);

    // Compute counts combining both Request tab and Credit Limit tab
    const creditLimitTabCount = useMemo(() => {
        if (allCountData?.creditLimit !== undefined && allCountData?.creditLimit !== null) {
            return Number(allCountData.creditLimit) || 0;
        }
        return Array.isArray(creditLimitData) ? creditLimitData.length : 0;
    }, [allCountData, creditLimitData]);

    const requestTabCount = useMemo(() => {
        if (Array.isArray(requestsData) && requestsData.length > 0) {
            return requestsData.length;
        }
        if (allCountData?.creditLimitRequests !== undefined && allCountData?.creditLimitRequests !== null) {
            return Number(allCountData.creditLimitRequests) || 0;
        }
        return Array.isArray(requestsData) ? requestsData.length : 0;
    }, [requestsData, allCountData]);

    const totalCreditLimitSidebarCount = creditLimitTabCount + requestTabCount;

    const privilegeCodes = user?.privilege?.map(p => p.privilegeCode) || [];

    // 🔹 Helper to render icon based on active route
    const icon = (menuItemKey, activeIcon, inactiveIcon) => {
        const path = location.pathname;
        let isActive = false;

        if (path === menuItemKey) isActive = true;

        if (menuItemKey === '/user-role-management' && path.startsWith('/user-role-management')) {
            isActive = true;
        }

        if (path.startsWith(menuItemKey + '/')) {
            isActive = true;
        }

        return <Image src={isActive ? activeIcon : inactiveIcon} preview={false} />;
    };

    const allMenuItems = [
        {
            key: 'user-role-management',
            icon: icon('/user-role-management', UserRoleMngtActiveIcon, UserRoleMngtIcon),
            label: 'User Role Management',
            accessCode: PERM_CODE.URACC,
            children: [
                {
                    key: '/user-role-management/manage-users',
                    icon: icon('/user-role-management/manage-users', UsersActiveIcon, UsersIcon),
                    label: 'Manage Users',
                },
                {
                    key: '/user-role-management/manage-rm',
                    icon: icon('/user-role-management/manage-rm', RmActiveIcon, RmIcon),
                    label: 'Manage RMs',
                },
            ],
        },
        {
            key: '/enquiry-management',
            icon: icon('/enquiry-management', EnqMngtActiveIcon, EnqMngtIcon),
            label: `Enquiry Management (${allCountData?.enquiryMgmt || 0})`,
            accessCode: PERM_CODE.EMACC
        },
        {
            key: '/business',
            icon: icon('/business', BusinessActiveIcon, BusinessIcon),
            label: `Business (${allCountData?.business || 0})`,
            accessCode: PERM_CODE.BUACC
        },
        {
            key: '/investor',
            icon: icon('/investor', InvestorActiveIcon, InvestorIcon),
            label: `Investor (${allCountData?.investor || 0})`,
            accessCode: PERM_CODE.IVACC
        },
        {
            key: '/enterprise',
            icon: icon('/enterprise', EntActiveIcon, EntIcon),
            label: `Enterprise  (${allCountData?.enterprise || 0})`,
            accessCode: PERM_CODE.ENACC,
        },
        {
            key: '/credit-limit',
            icon: icon('/credit-limit', CreditLimitActiveIcon, CreditLimitIcon),
            label: `Credit Limit (${totalCreditLimitSidebarCount})`,
            accessCode: PERM_CODE.CLACC,
        },
        {
            key: '/deals-upload',
            icon: icon('/deals-upload', InvUploadActiveIcon, InvUploadIcon),
            label: 'Deals Upload',
            accessCode: PERM_CODE.IUACC,
        },
        {
            key: '/deals-management',
            icon: icon('/deals-management', DealsActiveIcon, DealsIcon),
            label: `Deals Management (${allCountData?.deals || 0})`,
            accessCode: PERM_CODE.DLACC,
        },
        {
            key: '/settlements',
            icon: icon('/settlements', SettlementActiveIcon, SettlementIcon),
            label: 'Settlements',
            accessCode: PERM_CODE.SLACC,
        },
        {
            key: '/payments',
            icon: icon('/payments', PaymentActiveIcon, PaymentIcon),
            label: 'Payments',
            accessCode: PERM_CODE.PYACC,
        },
        {
            key: '/bank-operations',
            icon: icon('/bank-operations', BankActiveIcon, BankIcon),
            label: 'Bank Operations',
            accessCode: PERM_CODE.PYACC,
        },
        {
            key: '/wallets',
            icon: icon('/wallets', WalletActiveIcon, WalletIcon),
            label: 'Wallets',
            accessCode: PERM_CODE.WLACC,
        },
        {
            key: '/reports',
            icon: icon('/reports', ReportsActiveIcon, ReportsIcon),
            label: 'Reports',
            accessCode: PERM_CODE.RTACC,
        },
        {
            key: '/question-bank',
            icon: icon('/question-bank', EnqMngtActiveIcon, EnqMngtIcon),
            label: `Questionnaire`,
            accessCode: PERM_CODE.URACC,
        },
    ];

    const filterItemsByPrivilege = (items) => {
        return items.filter(item => !item.accessCode || privilegeCodes?.includes(item.accessCode));
    };

    const filteredMenuItems = filterItemsByPrivilege(allMenuItems);

    // Find closest matching key for nested routes
    const getSelectedKey = () => {
        const path = location.pathname;
        const allItems = filteredMenuItems.flatMap(item => item.children ? item.children : [item]);
        const matched = allItems.find(i => path.startsWith(i.key));
        return matched ? matched.key : path;
    };

    useEffect(() => {
        setSelectedKey(getSelectedKey());
    }, [location.pathname]);

    const handleMenuItemClick = (e) => {
        if (e.key.startsWith('/')) {
            navigate(e.key);
            onClose?.();
        }
    };

    return (
        <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            className="custom-menu"
            onClick={handleMenuItemClick}
            items={filteredMenuItems.map(item => ({
                key: item.key,
                icon: item.icon,
                label: item.label,
                children: item.children,
            }))}
        />
    );
};

export default SidebarContent;
